import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLanguage } from '@/state/language/LanguageProvider';
import { toast } from 'react-hot-toast';
import { DocumentDuplicateIcon, EyeIcon, PencilSquareIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { AeIconButton, AePageHeader, AePageShell } from '@/components/assessment-engine/layout';
import {
  AE_CARD_LIST,
  AE_FILTER_TAB,
  AE_FILTER_TAB_ACTIVE,
  AE_ICON_BTN,
  AE_ICON_SIZE,
  AE_LINK,
  AE_MUTED,
} from '@/components/assessment-engine/layout/aeTheme';
import {
  resolveTemplateStatusTransition,
} from '@/components/assessment-engine/templates/templateStatusOptions';
import { TemplateStatusSelect } from '@/components/assessment-engine/templates/TemplateStatusSelect';
import { formatTemplateListDate } from '@/components/assessment-engine/templates/templateListUtils';
import { assessmentEngineService } from '@/services/assessmentEngineService';
import { useAssessmentPreview } from '@/components/assessment-engine/preview';
import type { AeTemplateStatus, AeTemplateSummary } from '@/types/assessmentEngine';

const STATUS_TABS: Array<AeTemplateStatus | 'all'> = ['published', 'draft', 'retired', 'all'];

const TEMPLATE_LIST_ACTION_SPACER = 'inline-block h-8 w-8 shrink-0';
const TEMPLATE_LIST_ICON_BTN = `${AE_ICON_BTN} !h-8 !w-8 !p-1 shrink-0 box-border`;

/** Platform admin — list and manage assessment templates. */
export const TemplateListPage: React.FC = () => {
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const { openTemplatePreview } = useAssessmentPreview();
  const [templates, setTemplates] = useState<AeTemplateSummary[]>([]);
  const [statusFilter, setStatusFilter] = useState<AeTemplateStatus | 'all'>('published');
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [statusSelectKeys, setStatusSelectKeys] = useState<Record<string, number>>({});

  const load = useCallback(() => {
    setLoading(true);
    assessmentEngineService
      .listTemplates(statusFilter === 'all' ? undefined : statusFilter)
      .then(setTemplates)
      .catch(() => toast.error(t('assessmentEngine.templates.failedLoad')))
      .finally(() => setLoading(false));
  }, [statusFilter, t]);

  useEffect(() => {
    load();
  }, [load]);

  const handleNew = async () => {
    const code = `TPL-${Date.now().toString(36).slice(-6).toUpperCase()}`;
    try {
      const created = await assessmentEngineService.createTemplate({
        code,
        name: t('assessmentEngine.templates.newName'),
      });
      navigate(`/assessment-engine/templates/${created.id}`);
    } catch {
      toast.error(t('assessmentEngine.templates.createFailed'));
    }
  };

  const runAction = async (id: string, action: () => Promise<unknown>, successKey: string) => {
    setBusyId(id);
    try {
      await action();
      toast.success(t(successKey));
      load();
    } catch {
      toast.error(t('assessmentEngine.templates.actionFailed'));
    } finally {
      setBusyId(null);
    }
  };

  const confirmStatusChange = (tpl: AeTemplateSummary, transition: 'publish' | 'retire' | 'republish') => {
    const key =
      transition === 'publish'
        ? 'assessmentEngine.templates.publishConfirm'
        : transition === 'retire'
          ? 'assessmentEngine.templates.retireConfirm'
          : 'assessmentEngine.templates.republishConfirm';
    return window.confirm(t(key, { name: tpl.name }));
  };

  const applyStatusChange = async (tpl: AeTemplateSummary, nextStatus: AeTemplateStatus) => {
    const transition = resolveTemplateStatusTransition(
      (tpl.status ?? 'draft') as AeTemplateStatus,
      nextStatus
    );
    if (!transition) return;

    if (!confirmStatusChange(tpl, transition)) {
      setStatusSelectKeys((prev) => ({
        ...prev,
        [tpl.id]: (prev[tpl.id] ?? 0) + 1,
      }));
      return;
    }

    const action =
      transition === 'publish'
        ? () => assessmentEngineService.publishTemplate(tpl.id)
        : transition === 'retire'
          ? () => assessmentEngineService.retireTemplate(tpl.id)
          : () => assessmentEngineService.republishTemplate(tpl.id);

    const successKey =
      transition === 'publish'
        ? 'assessmentEngine.templates.published'
        : transition === 'retire'
          ? 'assessmentEngine.templates.retired'
          : 'assessmentEngine.templates.republished';

    await runAction(tpl.id, action, successKey);
  };

  const confirmDelete = (tpl: AeTemplateSummary) => {
    if (!window.confirm(t('assessmentEngine.templates.deleteConfirm'))) return;
    void runAction(
      tpl.id,
      () => assessmentEngineService.deleteTemplate(tpl.id),
      'assessmentEngine.templates.deleted'
    );
  };

  return (
    <AePageShell>
      <AePageHeader
        title={t('assessmentEngine.templates.title')}
        actions={
          <AeIconButton
            label={t('assessmentEngine.templates.new')}
            onClick={() => void handleNew()}
            variant="primary"
          >
            <PlusIcon className={AE_ICON_SIZE} aria-hidden />
          </AeIconButton>
        }
      />

      <div className="flex flex-wrap gap-2 mb-4">
        {STATUS_TABS.map((tab) => (
          <button
            key={tab}
            type="button"
            onClick={() => setStatusFilter(tab)}
            className={statusFilter === tab ? AE_FILTER_TAB_ACTIVE : AE_FILTER_TAB}
          >
            {t(`assessmentEngine.templates.statusTab.${tab}`)}
          </button>
        ))}
      </div>

      {loading ? (
        <p className={AE_MUTED}>{t('assessmentEngine.templates.loading')}</p>
      ) : templates.length === 0 ? (
        <p className={AE_MUTED}>{t('assessmentEngine.templates.empty')}</p>
      ) : (
        <ul className={`${AE_CARD_LIST} w-full max-w-full overflow-hidden`}>
          {templates.map((tpl) => {
            const isDraft = tpl.status === 'draft';
            const templateStatus = (tpl.status ?? 'draft') as AeTemplateStatus;

            return (
              <li
                key={tpl.id}
                className="grid min-w-0 max-w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-2 overflow-hidden px-3 py-2"
              >
                <div className="min-w-0 overflow-hidden">
                  <div className="flex min-w-0 items-center gap-2 overflow-hidden">
                    <Link
                      to={`/assessment-engine/templates/${tpl.id}`}
                      className={`${AE_LINK} min-w-0 truncate`}
                    >
                      {tpl.name}
                    </Link>
                    <span className="hidden shrink-0 text-xs text-[var(--color-secondary-text)] sm:inline">
                      {tpl.code}
                    </span>
                  </div>
                  <p className="mt-0.5 truncate text-xs text-[var(--color-secondary-text)]">
                    <span>
                      {t('assessmentEngine.templates.createdLabel')}:{' '}
                      {formatTemplateListDate(tpl.created_at, currentLanguage.id)}
                    </span>
                    <span className="mx-1.5 text-[var(--color-border)]" aria-hidden>
                      ·
                    </span>
                    <span>
                      {t('assessmentEngine.templates.lastActivityLabel')}:{' '}
                      {formatTemplateListDate(tpl.updated_at, currentLanguage.id)}
                    </span>
                  </p>
                </div>
                <div className="flex max-w-full items-center justify-end gap-0.5">
                  <TemplateStatusSelect
                    rowId={tpl.id}
                    templateName={tpl.name}
                    status={templateStatus}
                    selectKey={statusSelectKeys[tpl.id] ?? 0}
                    disabled={busyId === tpl.id}
                    onStatusChange={(next) => void applyStatusChange(tpl, next)}
                  />
                  <AeIconButton
                    compact
                    label={t('assessmentEngine.preview.button')}
                    onClick={() => openTemplatePreview(tpl.id)}
                  >
                    <EyeIcon className={AE_ICON_SIZE} aria-hidden />
                  </AeIconButton>
                  <Link
                    to={`/assessment-engine/templates/${tpl.id}`}
                    className={TEMPLATE_LIST_ICON_BTN}
                    title={t('assessmentEngine.builder.edit')}
                    aria-label={t('assessmentEngine.builder.edit')}
                  >
                    <PencilSquareIcon className={AE_ICON_SIZE} aria-hidden />
                  </Link>
                  <AeIconButton
                    compact
                    label={t('assessmentEngine.templates.clone')}
                    disabled={busyId === tpl.id}
                    onClick={() =>
                      runAction(
                        tpl.id,
                        () =>
                          assessmentEngineService.cloneTemplate(tpl.id, {
                            code: `${tpl.code}-COPY`,
                            name: `${tpl.name} (copy)`,
                          }),
                        'assessmentEngine.templates.cloned'
                      )
                    }
                  >
                    <DocumentDuplicateIcon className={AE_ICON_SIZE} aria-hidden />
                  </AeIconButton>
                  {isDraft ? (
                    <AeIconButton
                      compact
                      label={t('assessmentEngine.templates.delete')}
                      disabled={busyId === tpl.id}
                      variant="danger"
                      onClick={() => confirmDelete(tpl)}
                    >
                      <TrashIcon className={AE_ICON_SIZE} aria-hidden />
                    </AeIconButton>
                  ) : (
                    <span className={TEMPLATE_LIST_ACTION_SPACER} aria-hidden />
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </AePageShell>
  );
};
